import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bell, Plus, Trash2, Loader2, WifiOff } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { PortalDialogContent as DialogContent } from '../PortalDialogContent';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';

import portalApi from '../lib/portalApi';
import { PortalEmptyState } from '../ui/PortalEmptyState';
import { PortalDateTimePicker } from '../ui/PortalDateTimePicker';
import { errorText } from '../health/healthConfig';

const REPEATS = [
  { value: 'none',    label: 'One-time' },
  { value: 'daily',   label: 'Every day' },
  { value: 'weekly',  label: 'Every week' },
  { value: 'monthly', label: 'Every month' },
];

export default function PortalRemindersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen] = useState(false);

  // form
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [repeat, setRepeat] = useState('none');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true); setLoadError('');
    try {
      const r = await portalApi.get('/reminders');
      setRows(r.data.reminders || []);
    } catch (e) {
      console.error('Failed to load reminders:', e);
      setLoadError(errorText(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!title.trim() || !dueAt) {
      toast.error('Title and date are required.');
      return;
    }
    setSaving(true);
    try {
      await portalApi.post('/reminders', {
        title: title.trim(),
        description: desc.trim() || undefined,
        due_at: new Date(dueAt).toISOString(),
        repeat,
        channels: ['push'],
      });
      toast.success('Reminder saved');
      setTitle(''); setDesc(''); setDueAt(''); setRepeat('none');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save reminder');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this reminder?')) return;
    try {
      await portalApi.delete(`/reminders/${id}`);
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-5" data-testid="portal-reminders-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-6 h-6 text-emerald-600" />
          <h1 className="text-2xl font-bold text-slate-900">Reminders</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="portal-reminder-new">
              <Plus className="w-4 h-4 mr-1" /> New reminder
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="portal-reminder-dialog">
            <DialogHeader>
              <DialogTitle>New reminder</DialogTitle>
              <DialogDescription>Set a title, when it&apos;s due and whether it should repeat.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Take blood-pressure medication" data-testid="portal-reminder-title" />
              </div>
              <div>
                <Label>Details</Label>
                <Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="portal-reminder-desc" />
              </div>
              <div>
                <Label>When</Label>
                <PortalDateTimePicker value={dueAt} onChange={setDueAt} testId="portal-reminder-when" />
              </div>
              <div>
                <Label>Repeat</Label>
                <Select value={repeat} onValueChange={setRepeat}>
                  <SelectTrigger data-testid="portal-reminder-repeat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REPEATS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={create} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="portal-reminder-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Bell className="w-4 h-4 mr-1" />}
                Save reminder
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle>Your reminders</CardTitle></CardHeader>
        <CardContent>
          {loading && <div className="text-sm text-slate-500 py-6 text-center" data-testid="portal-reminders-loading">Loading reminders…</div>}
          {!loading && loadError && (
            <PortalEmptyState icon={WifiOff} title="Couldn't load your reminders" description={loadError}
              testId="portal-reminders-load-error"
              action={<Button variant="outline" size="sm" onClick={load} data-testid="portal-reminders-retry">Try again</Button>} />
          )}
          {!loading && !loadError && rows.length === 0 && (
            <div className="text-sm text-slate-400 py-6 text-center" data-testid="portal-reminders-empty">No reminders set. Tap &quot;New reminder&quot; to add one.</div>
          )}
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.reminder_id} className="border rounded-xl p-3 flex items-center gap-3" data-testid={`portal-reminder-${r.reminder_id}`}>
                <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700"><Bell className="w-4 h-4" /></div>
                <div className="flex-1">
                  <div className="font-medium">{r.title}</div>
                  <div className="text-xs text-slate-500">
                    {new Date(r.due_at).toLocaleString()}
                    {r.repeat && r.repeat !== 'none' && <> · repeats {r.repeat}</>}
                  </div>
                  {r.description && <div className="text-sm text-slate-600 mt-1">{r.description}</div>}
                </div>
                <Badge variant="outline">{r.repeat}</Badge>
                <Button variant="ghost" size="icon" onClick={() => remove(r.reminder_id)} data-testid={`portal-reminder-delete-${r.reminder_id}`}>
                  <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
